import { useMutation, useQueryClient } from "@tanstack/react-query";
import { suspendUser, activateUser } from "../../services/userService";
import { IUser } from "../../types";
import { useState } from "react";
import { ConfirmationModal } from "../modals/user-status";

interface UserActionsProps {
  user: IUser;
}

export const UserActions = ({ user }: UserActionsProps) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState<"activate" | "suspend" | null>(
    null
  );

  const suspendMutation = useMutation({
    mutationFn: () => suspendUser(user._id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", user._id] });
      setIsModalOpen(false);
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => activateUser(user._id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", user._id] });
      setIsModalOpen(false);
    },
  });

  const handleConfirm = () => {
    if (actionType === "suspend") {
      suspendMutation.mutate();
    } else if (actionType === "activate") {
      activateMutation.mutate();
    }
  };

  return (
    <div className="border shadow-sm p-6 rounded-lg">
      <p className="text-xl mb-2">Actions</p>
      <div className="flex items-center justify-between">
        <div className="flex space-x-2 items-center">
          <span className="text-gray-500 text-sm">User status:</span>
          <span
            className={`capitalize ${
              user.userStatus === "activated"
                ? "text-emerald-500"
                : "text-red-500"
            }`}
          >
            {user.userStatus}
          </span>
        </div>

        {user.userStatus === "activated" ? (
          <button
            onClick={() => {
              setActionType("suspend");
              setIsModalOpen(true);
            }}
            className="border w-30 px-3 py-2 bg-transparent border-rose-500 text-rose-500 rounded-lg text-sm flex items-center justify-center"
          >
            Suspend
          </button>
        ) : (
          <button
            onClick={() => {
              setActionType("activate");
              setIsModalOpen(true);
            }}
            className="border w-30 px-3 py-2 bg-transparent border-green-500 text-green-500 rounded-lg text-sm flex items-center justify-center"
          >
            Activate
          </button>
        )}
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
        actionType={actionType as "activate" | "suspend"}
        userName={user.firstName}
        isLoading={suspendMutation.isPending || activateMutation.isPending}
      />
    </div>
  );
};
